import React from 'react';
import PropTypes from 'prop-types';
import { cva } from 'class-variance-authority';
import cn from '../utils/cn';
import styles from './Button.module.css';

const button = cva(styles.root, {
  variants: {
    variant: {
      primary: styles.primary,
      secondary: styles.secondary,
      dark: styles.dark,
      outline: styles.outline
    },
    shape: {
      sharp: styles.sharp
    },
    size: {
      default: styles.sizeDefault,
      small: styles.sizeSmall,
      md: styles.sizeMd
    },
    loading: {
      true: styles.loading
    }
  },
  defaultVariants: { variant: 'primary', size: 'default' }
});

const Button = React.forwardRef(function Button(
  {
    variant,
    shape,
    size,
    loading = false,
    disabled,
    onClick,
    as: Tag = 'button',
    type,
    className,
    children,
    ...rest
  },
  ref
) {
  const tagProps = Tag === 'button' ? { type: type ?? 'button' } : {};
  const handleClick = e => {
    if (loading || disabled) {
      e.preventDefault();
      return;
    }
    if (onClick) onClick(e);
  };
  const content =
    shape === 'sharp' ? (
      <span className={styles.sharpContent}>{children}</span>
    ) : (
      children
    );
  return (
    <Tag
      ref={ref}
      className={cn(
        button({ variant, shape, size, loading: loading ? 'true' : undefined }),
        className
      )}
      aria-busy={loading || undefined}
      disabled={Tag === 'button' ? disabled || loading : undefined}
      onClick={handleClick}
      {...tagProps}
      {...rest}>
      {loading && <span className={styles.spinner} aria-hidden='true' />}
      {content}
    </Tag>
  );
});

Button.propTypes = {
  variant: PropTypes.oneOf(['primary', 'secondary', 'dark', 'outline']),
  shape: PropTypes.oneOf(['sharp']),
  size: PropTypes.oneOf(['default', 'small', 'md']),
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  as: PropTypes.elementType,
  type: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node
};

export default Button;
